import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api/v1',
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve(token));
  failedQueue = [];
};

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('access_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Endpoints do próprio ciclo de autenticação — um 401 aqui nunca significa "token
// expirado, tenta renovar": login errado é credencial inválida (sem token nenhum
// ainda), refresh/logout já tratam o próprio erro. Nunca devem entrar na fila de
// retry, senão um 401 de login (sem refresh_token salvo) deixa `isRefreshing`
// travado em `true` pra sempre — a próxima tentativa de login fica pendurada
// (empilhada em `failedQueue`) esperando um `processQueue` que nunca chega.
const AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh', '/auth/logout'];

api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;
    const isAuthEndpoint = AUTH_ENDPOINTS.some(p => original?.url?.includes(p));
    if (err.response?.status === 401 && !original._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }
      original._retry = true;
      isRefreshing = true;
      const refresh = localStorage.getItem('refresh_token');
      if (!refresh) {
        isRefreshing = false;
        processQueue(err, null);
        window.dispatchEvent(new Event('auth:logout'));
        return Promise.reject(err);
      }
      try {
        // D8-fix: usa a baseURL da instância `api` para funcionar tanto com
        // proxy relativo (/api/v1) quanto com URL absoluta (REACT_APP_API_URL)
        const { data } = await axios.post(`${api.defaults.baseURL}/auth/refresh`, { refresh_token: refresh });
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        processQueue(null, data.access_token);
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return api(original);
      } catch (e) {
        processQueue(e, null);
        window.dispatchEvent(new Event('auth:logout'));
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(err);
  }
);

export default api;
